import React from 'react';
import styles from "./styles.module.scss";
import { CloseCircleGreyIcon } from 'assets';
import * as yup from 'yup';

export type ResponsibilityFormData = {
  title: string;
  description: string;
  checked: boolean;
}[];

export type TechnicalSkillFormData = {
  title: string;
  expectation: string;
}[];

export type SoftSkillFormData = {
  title: string;
  description: string;
  checked: boolean;
  rating: number;
}[];

export type WorkCultureTraitFormData = {
  title: string;
  description: string;
  checked: boolean;
}[];

export type WorkStyleFormData = {
  description: string;
  rating: number;
}[];

export type PersonalityAlignmentFormData = {
  category: string;
  description: string;
  style: string;
  implication: string;
}[];

export const responsibilitySchema = yup.array().of(
  yup.object().shape({
    title: yup.string().required('Title is required'),
    description: yup.string().required('Description is required'),
  })
);

export const technicalSkillSchema = yup.array().of(
  yup.object().shape({
    title: yup.string().required('Skill name is required'),
    expectation: yup.string().required('Expectation is required'),
  })
);

export const softSkillSchema = yup.array().of(
  yup.object().shape({
    title: yup.string().required('Skill name is required'),
    description: yup.string().required('Description is required'),
    rating: yup.number().min(1).max(5).required('Rating is required'),
  })
);

export const workCultureTraitSchema = yup.array().of(
  yup.object().shape({
    title: yup.string().required('Trait name is required'),
    description: yup.string().required('Description is required'),
  })
);

export const workStyleSchema = yup.array().of(
  yup.object().shape({
    description: yup.string().required('Description is required'),
    rating: yup.number().min(1).max(7).required('Rating is required'),
  })
);

export const personalityAlignmentSchema = yup.array().of(
  yup.object().shape({
    category: yup.string().required('Category is required'),
    description: yup.string().required('Description is required'),
    style: yup.string().required('Style is required'),
    implication: yup.string().required('Implication is required'),
  })
);

// This modal defaults to "Edit <title>" because most of its uses are criteria
// editors. Titles that read as their own heading opt out here.
const STANDALONE_TITLES = ['Start Assessment', 'Submit Assessment', 'Editing Details', 'Confirm Salary'];

interface BaseEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitch?: () => void;
  title: string;
  onSubmit: (e: any) => void;
  children: React.ReactNode;
  cvType?: boolean;
  cancelButtonText?: string;
  saveButtonText?: string;
  overlayClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  closeButtonClassName?: string;
  formClassName?: string;
  formContentClassName?: string;
  footerClassName?: string;
  cancelButtonClassName?: string;
  saveButtonClassName?: string;
  isSubmitting?: boolean;
  icon?: React.ReactNode;
  hasIcon?: boolean;
}

export const BaseEditModal: React.FC<BaseEditModalProps> = ({
  isOpen,
  onClose,
  onSwitch,
  title,
  onSubmit,
  children,
  cvType,
  cancelButtonText = 'Cancel',
  saveButtonText = 'Save Changes',
  overlayClassName,
  contentClassName,
  headerClassName,
  titleClassName,
  closeButtonClassName,
  formClassName,
  formContentClassName,
  footerClassName,
  cancelButtonClassName,
  saveButtonClassName,
  isSubmitting = false,
  icon,
  hasIcon = false,
}) => {
  if (!isOpen) return null;

  return (
    // Close only when the backdrop itself is clicked, not portaled children (e.g. react-select menus)
    <div
      className={`${styles.modalOverlay} ${overlayClassName || ''}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`${styles.modalContent} ${contentClassName || ''}`}>
        <div className={`${styles.modalHeader} ${headerClassName || ''}`}>
          <h2 className={`${styles.modalTitle} ${titleClassName || ''}`}>
            {STANDALONE_TITLES.includes(title) ? title : `Edit ${title}`}
          </h2>

          <button onClick={onClose} className={`${styles.closeButton} ${closeButtonClassName || ''}`}>
            <CloseCircleGreyIcon className={styles.closeIcon} />
          </button>
        </div>

        <form onSubmit={onSubmit} className={`${styles.formContainer} ${formClassName || ''}`}>
          <div className={`${styles.formContent} ${formContentClassName || ''}`}>
            {children}
          </div>
        </form>
        <div className={`${styles.modalFooter} ${footerClassName || ''} ${cancelButtonText === '' && styles.shiftButton}`}>
          {cancelButtonText && <button
            className={`${styles.cancelButton} ${styles.button} ${cancelButtonClassName || ''}`}
            onClick={() => {
              if (cvType) {
                if (onSwitch) onSwitch();
              } else {
                onClose();
              }
            }}
          >
            {cancelButtonText}
          </button>}
          <button onClick={onSubmit} className={`${styles.saveButton} ${styles.button} ${saveButtonClassName || ''}`}>
            {hasIcon && icon}
            {isSubmitting ? 'Saving...' : saveButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};